/**
 * Cold-provider entry: patient shared CMR via URL QR (external camera).
 * MSI3 narrative → sign-in → consent (terms, privacy, draft BAA + stored render, phone) → redeem token → ChartMind recording.
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Link,
  Paper,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { AuthContext } from '@healthdesk/shared-hooks';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, clinicalContactService, firebaseApp } from '@healthdesk/shared-services';
import { contentCardSx } from '../../styles/standaloneLayoutTokens';
import {
  PROVIDER_CMR_TERMS_VERSION,
  PROVIDER_CMR_PRIVACY_VERSION,
} from '../../constants/providerCmrConsent';
import { COLD_PATH_BAA_DOCUMENT_VERSION } from '../../constants/coldPathBaa';
import { buildColdPathBaaText } from '../../legal/coldPathBaaTemplate';
import { getFirebaseHttpsCallableUserMessage } from '../../utils/firebaseHttpsCallableUserMessage';
import ProviderCmrBaaViewer from './ProviderCmrBaaViewer';
import { fetchPatientCMR } from '../../features/chartmind/components/chartmind/services/cmrReadService';
import { buildPatientInfoFromCmrShare } from '../../features/chartmind/components/chartmind/services/cmrSharePatientInfo';

const LINKED_PATIENT_STORAGE_KEY = 'chartmind_linked_patient';

const CHARTMIND_ROLES = new Set(['professional-chartmind', 'pro2']);

function getEffectiveRole(userData) {
  if (!userData) return null;
  if (userData.role) return userData.role;
  const sg = userData.systemGroupRoles;
  if (Array.isArray(sg) && sg[0]?.role) return sg[0].role;
  return null;
}

function hasRecordedConsent(userData) {
  if (!userData) return false;
  const baaOk =
    !!userData.providerCmrBaaAcceptedAt || !!userData.providerCmrBaaPlaceholderAcceptedAt;
  return !!(
    userData.providerCmrTermsAcceptedAt &&
    userData.providerCmrPrivacyAcceptedAt &&
    baaOk
  );
}

const KEY_MESSAGE =
  "You didn't ask to be part of this. Neither did we — we selected your institution because your training environment already reflects the kind of clinical culture where a tool like MSI3 can be used well. That means you: someone who questions things, supervises carefully, and doesn't let any tool replace your judgment.";

const SUPPORTING_MESSAGES = [
  "MSI3 is not here to tell you what to diagnose. It's here to reduce the cognitive load between you and that decision — documentation, differential generation, literature synthesis — so that your judgment operates on better information, with more bandwidth.",
  'Every output MSI3 generates passes through you. You are the error-catcher, the equity-enforcer, the edge-case handler, the values-holder, the consent-protector, and the context-reader. The AI handles computation. You handle everything that makes care human.',
  "We're not asking you to trust it. We're asking you to try it with appropriate skepticism — which is exactly how you should approach any new tool.",
  "You can use it, ignore it, or ask your resident to engage with it while you supervise. Your call. Always.",
  "You already know what it costs when a patient's history doesn't follow them into your department. MSI3's CMR is patient-controlled, instantly accessible, grantable at the point of care.",
];

const ProviderCmrSharePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenId = searchParams.get('token')?.trim() || '';
  const { user, userData, loading: authLoading } = useContext(AuthContext);

  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [baa, setBaa] = useState(false);
  const [enrolleeLegalName, setEnrolleeLegalName] = useState('');
  const [enrolleeAddress, setEnrolleeAddress] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const legalNamePrefilledRef = useRef(false);

  useEffect(() => {
    if (legalNamePrefilledRef.current || !userData) return;
    const combined = [userData.name, userData.lastName].filter(Boolean).join(' ').trim();
    if (combined) {
      setEnrolleeLegalName(combined);
      legalNamePrefilledRef.current = true;
    }
  }, [userData]);

  const baaPreviewText = useMemo(
    () =>
      buildColdPathBaaText({
        enrolleeLegalName: enrolleeLegalName.trim() || '(your legal name — enter below)',
        enrolleeAddress:
          enrolleeAddress.trim() || 'As associated with your account in the Services, unless you provide an address.',
        effectiveDateDisplay:
          '(Set automatically to the date and time you click “Accept and open ChartMind”.)',
        termsVersion: PROVIDER_CMR_TERMS_VERSION,
        privacyVersion: PROVIDER_CMR_PRIVACY_VERSION,
      }),
    [enrolleeLegalName, enrolleeAddress]
  );

  const effectiveRole = useMemo(() => getEffectiveRole(userData), [userData]);
  const consentDone = useMemo(() => hasRecordedConsent(userData), [userData]);
  const onboardingDone = !!userData?.providerCmrOnboardingCompletedAt;
  const needsPhoneCapture = user && !user.phoneNumber && !userData?.phone;

  const authReturnPath = useMemo(() => {
    if (!tokenId) return '/provider/cmr-share';
    return `/provider/cmr-share?token=${encodeURIComponent(tokenId)}`;
  }, [tokenId]);

  const signInHref = `/auth?role=chartmind-provider&returnTo=${encodeURIComponent(authReturnPath)}`;

  const runRedeemAndEnterChartMind = useCallback(async () => {
    if (!user?.uid || !tokenId) return;
    setSubmitting(true);
    setError('');
    try {
      const functions = getFunctions();
      const redeem = httpsCallable(functions, 'redeemPatientCmrShareToken');
      const { data } = await redeem({ tokenId });
      if (!data?.ok || !data.patientUserId) {
        throw new Error(data?.message || 'Could not open this patient share.');
      }

      const patientUserId = data.patientUserId;
      const allowedData = data.allowedData || [];
      const patientName = data.patientName || null;
      const patientDob = data.patientDob || null;

      try {
        await clinicalContactService.recordClinicalContact({
          patientUserId,
          facilityUserId: userData?.facilityId || user.uid,
          providerId: user.uid,
          contactType: 'chartmind_cmr_scan',
          patientDataAccessed: allowedData,
        });
      } catch (contactErr) {
        console.warn('[ProviderCmrShare] clinical contact log failed:', contactErr);
      }

      let cmrData = null;
      try {
        cmrData = await fetchPatientCMR(patientUserId);
      } catch (cmrErr) {
        console.warn('[ProviderCmrShare] fetchPatientCMR failed (non-fatal):', cmrErr);
      }

      const patientInfo = buildPatientInfoFromCmrShare({
        patientUserId,
        patientName,
        patientDob,
        allowedData,
        cmrData,
      });

      try {
        sessionStorage.setItem(LINKED_PATIENT_STORAGE_KEY, JSON.stringify(patientInfo));
      } catch (storageErr) {
        console.warn('[ProviderCmrShare] sessionStorage failed:', storageErr);
      }

      await db
        .collection('users')
        .doc(user.uid)
        .set(
          {
            providerCmrOnboardingCompletedAt: firebase.firestore.FieldValue.serverTimestamp(),
          },
          { merge: true }
        );

      navigate('/dashboard/chartmind/recording', { replace: true });
    } catch (err) {
      console.error('[ProviderCmrShare] redeem flow failed:', err);
      const { userMessage, code } = getFirebaseHttpsCallableUserMessage(err);
      const isFirebaseCallable = typeof code === 'string' && code.startsWith('functions/');
      const msg = isFirebaseCallable
        ? userMessage
        : (typeof err?.message === 'string' && err.message.trim()
            ? err.message
            : userMessage ||
              (typeof err?.details === 'string' ? err.details : null) ||
              'Something went wrong. Try again or ask the patient for a new share code.');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [navigate, tokenId, user, userData?.facilityId]);

  const handleConsentAndContinue = useCallback(async () => {
    if (!user?.uid || !tokenId) return;
    if (!terms || !privacy || !baa) {
      setError('Please accept the Terms of Use, Privacy Policy, and Business Associate Agreement.');
      return;
    }
    const legal = enrolleeLegalName.trim();
    if (!legal) {
      setError('Enter your full legal name as it should appear on the Business Associate Agreement.');
      return;
    }
    if (needsPhoneCapture && !contactPhone.trim()) {
      setError('Please enter a phone number so we can reach you about your account.');
      return;
    }

    const acceptedAtLocal = new Date().toLocaleString(undefined, {
      dateStyle: 'long',
      timeStyle: 'short',
    });
    const renderedAgreement = buildColdPathBaaText({
      enrolleeLegalName: legal,
      enrolleeAddress:
        enrolleeAddress.trim() || 'As associated with the Covered Entity account in the Services.',
      effectiveDateDisplay: acceptedAtLocal,
      termsVersion: PROVIDER_CMR_TERMS_VERSION,
      privacyVersion: PROVIDER_CMR_PRIVACY_VERSION,
    });

    setSubmitting(true);
    setError('');
    try {
      const updates = {
        providerCmrTermsAcceptedAt: firebase.firestore.FieldValue.serverTimestamp(),
        providerCmrPrivacyAcceptedAt: firebase.firestore.FieldValue.serverTimestamp(),
        providerCmrBaaAcceptedAt: firebase.firestore.FieldValue.serverTimestamp(),
        providerCmrTermsVersion: PROVIDER_CMR_TERMS_VERSION,
        providerCmrPrivacyVersion: PROVIDER_CMR_PRIVACY_VERSION,
        providerCmrBaaDocumentVersion: COLD_PATH_BAA_DOCUMENT_VERSION,
        providerCmrBaaRenderedAgreement: renderedAgreement,
        providerCmrEnrolleeLegalName: legal,
        providerCmrEnrolleeProfessionalAddress: enrolleeAddress.trim() || null,
      };

      if (needsPhoneCapture && contactPhone.trim()) {
        updates.phone = contactPhone.trim();
      }

      if (effectiveRole === 'professional') {
        updates.role = 'professional-chartmind';
      }

      await db.collection('users').doc(user.uid).set(updates, { merge: true });

      await runRedeemAndEnterChartMind();
    } catch (err) {
      console.error('[ProviderCmrShare] consent save failed:', err);
      setError(err.message || 'Could not save your responses. Try again.');
      setSubmitting(false);
    }
  }, [
    user?.uid,
    tokenId,
    terms,
    privacy,
    baa,
    enrolleeLegalName,
    enrolleeAddress,
    needsPhoneCapture,
    contactPhone,
    effectiveRole,
    runRedeemAndEnterChartMind,
  ]);

  const handleProfessionalUpgradeAndRedeem = useCallback(async () => {
    if (!user?.uid) return;
    setSubmitting(true);
    setError('');
    try {
      await db.collection('users').doc(user.uid).set({ role: 'professional-chartmind' }, { merge: true });
      await runRedeemAndEnterChartMind();
    } catch (err) {
      console.error('[ProviderCmrShare] professional upgrade failed:', err);
      setError(err.message || 'Could not enable ChartMind access.');
      setSubmitting(false);
    }
  }, [user?.uid, runRedeemAndEnterChartMind]);

  if (!tokenId) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="warning">
          This link is missing a share token. Ask the patient to show the QR code or link again from Share Health
          Records.
        </Alert>
      </Container>
    );
  }

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={0} sx={{ ...contentCardSx, p: 3 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            A patient shared their health record with you
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            MSI3 (Medical SuperIntelligence cubed) helps clinicians document and think with better context — with you
            staying in charge of every clinical decision.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, fontStyle: 'italic' }}>
            {KEY_MESSAGE}
          </Typography>
          <Divider sx={{ my: 3 }} />
          {SUPPORTING_MESSAGES.map((msg) => (
            <Typography key={msg.slice(0, 40)} variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {msg}
            </Typography>
          ))}
          <Button component={RouterLink} to={signInHref} variant="contained" size="large" fullWidth sx={{ mt: 2 }}>
            Continue with phone sign-in
          </Button>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2, textAlign: 'center' }}>
            You will verify your phone number, accept terms, and open ChartMind with this patient linked.
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (userData?.role === 'patient' || effectiveRole === 'patient') {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="info">
          You are signed in as a patient account. Sign out and use a clinician account, or open this link in a browser
          where you can sign in as a healthcare provider.
        </Alert>
        <Button sx={{ mt: 2 }} onClick={() => firebaseApp.auth().signOut()}>
          Sign out
        </Button>
      </Container>
    );
  }

  if (effectiveRole && !CHARTMIND_ROLES.has(effectiveRole) && effectiveRole !== 'professional') {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="warning">
          This patient share flow is for MSI3 ChartMind clinicians. Your current role ({effectiveRole}) is not
          eligible. Contact your administrator if you need access.
        </Alert>
      </Container>
    );
  }

  const showFullNarrative = !onboardingDone;
  const isChartmind = CHARTMIND_ROLES.has(effectiveRole);
  const isProfessional = effectiveRole === 'professional';
  const showRedeemOnly = isChartmind && consentDone;
  const showProfessionalUpgrade = isProfessional && consentDone;
  const showConsentForm = !consentDone;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ ...contentCardSx, p: 3 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          {showFullNarrative ? 'Welcome to MSI3' : 'Patient share ready'}
        </Typography>

        {showFullNarrative && (
          <>
            <Typography variant="body1" sx={{ mb: 2, fontStyle: 'italic' }}>
              {KEY_MESSAGE}
            </Typography>
            <Divider sx={{ my: 2 }} />
            {SUPPORTING_MESSAGES.map((msg) => (
              <Typography key={msg.slice(0, 48)} variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {msg}
              </Typography>
            ))}
            <Divider sx={{ my: 3 }} />
          </>
        )}

        {showRedeemOnly && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your agreements are on file. Continue to open this patient in ChartMind recording.
            </Typography>
            <LoadingButton
              variant="contained"
              size="large"
              fullWidth
              loading={submitting}
              onClick={runRedeemAndEnterChartMind}
            >
              Open ChartMind with this patient
            </LoadingButton>
          </Box>
        )}

        {showProfessionalUpgrade && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Your legal agreements are already recorded. Enable ChartMind clinician access to open this patient
              record.
            </Alert>
            <LoadingButton
              variant="contained"
              size="large"
              fullWidth
              loading={submitting}
              onClick={handleProfessionalUpgradeAndRedeem}
            >
              Enable ChartMind and open patient
            </LoadingButton>
          </Box>
        )}

        {showConsentForm && (
          <>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Before we open the record
            </Typography>
            {isProfessional && (
              <Alert severity="info" sx={{ mb: 2 }}>
                We will upgrade your account to ChartMind clinician access so you can use MSI3 with this patient.
              </Alert>
            )}

            {needsPhoneCapture && (
              <TextField
                label="Your phone number"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                fullWidth
                required
                margin="normal"
                helperText="Required for your clinician account."
              />
            )}

            <TextField
              label="Your full legal name (Covered Entity signatory)"
              value={enrolleeLegalName}
              onChange={(e) => setEnrolleeLegalName(e.target.value)}
              fullWidth
              required
              margin="normal"
              helperText="As it should appear on the Business Associate Agreement. MD3C, LLC has signed a master framework; you accept the same terms electronically."
            />
            <TextField
              label="Professional address (optional)"
              value={enrolleeAddress}
              onChange={(e) => setEnrolleeAddress(e.target.value)}
              fullWidth
              margin="normal"
              multiline
              minRows={2}
              helperText="Practice or primary professional address. If omitted, a default line is used in the stored agreement."
            />

            <FormControlLabel
              control={<Checkbox checked={terms} onChange={(e) => setTerms(e.target.checked)} />}
              label={
                <Typography variant="body2">
                  I agree to the{' '}
                  <Link href="/terms-of-use" target="_blank" rel="noreferrer">
                    Terms of Use
                  </Link>
                  .
                </Typography>
              }
            />
            <FormControlLabel
              control={<Checkbox checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} />}
              label={
                <Typography variant="body2">
                  I agree to the{' '}
                  <Link href="/privacy-policy" target="_blank" rel="noreferrer">
                    Privacy Policy
                  </Link>
                  .
                </Typography>
              }
            />

            <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2, mb: 0.5 }}>
              Business Associate Agreement (DRAFT)
            </Typography>
            <Alert severity="warning" sx={{ mb: 1 }}>
              This agreement is marked DRAFT until counsel approves the language. A copy of the text you see (with your
              name and effective date) is stored with your account when you accept.
            </Alert>
            <ProviderCmrBaaViewer text={baaPreviewText} />

            <FormControlLabel
              sx={{ mt: 2, alignItems: 'flex-start' }}
              control={<Checkbox checked={baa} onChange={(e) => setBaa(e.target.checked)} />}
              label={
                <Typography variant="body2">
                  I have read the draft Business Associate Agreement above (including the DRAFT marking) and agree to enter
                  into this Agreement with MD3C, LLC as Business Associate, under Illinois law, with breach notification
                  without unreasonable delay. I confirm my legal name is accurate.
                </Typography>
              }
            />

            <LoadingButton
              variant="contained"
              size="large"
              fullWidth
              sx={{ mt: 3 }}
              loading={submitting}
              onClick={handleConsentAndContinue}
            >
              Accept and open ChartMind
            </LoadingButton>
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default ProviderCmrSharePage;
